import React from 'react';

import { store, useAppSelector } from '@/app/store';

import Button from '@/components/Common/Button';
import DigitInput from '@/components/Common/DigitInput';
import Input from '@/components/Common/Input';

import EnrollTwoFA from '@/pages/Login/EnrollTwoFA';
import {
  startVerifyEmail,
  verifyEmail,
  requestEmailChange,
  confirmEmailChange,
} from '@/features/logins/emailApi';
import { useResendCooldown } from '@/pages/Account/useCountdown';

const RESEND_COOLDOWN_SECONDS = 30;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const sixDigitRegex = /^\d{6}$/;

// OnboardingWizard is the mandatory first-login flow, in order:
// (1) confirm/verify the account email, then (2) choose a 2FA method —
// either enroll an authenticator app, OR opt into email login codes
// (available once the email is verified). It is mounted by App.tsx
// whenever a logged-in, non-exempt user has not yet completed onboarding.
// Step is DERIVED from Redux (email_verified / totp_enrolled /
// preferred_2fa_method) so a page refresh re-enters at the correct step.
//
// Completing step 2 satisfies the App.tsx gate when either an authenticator
// is enrolled (totp_enrolled) OR the email method has been recorded
// (preferred_2fa_method === 'email'). The user can still switch factors
// freely at later logins from the 2FA screen.
const OnboardingWizard: React.FC = () => {
  const login = useAppSelector((state) => state.logins.login);

  // Email first; once verified, advance to the 2FA method choice.
  const emailVerified = !!login?.email_verified;
  const step: 'email' | 'method' = emailVerified ? 'method' : 'email';

  // Step 2 — let the user pick how they want their login code.
  if (step === 'method') {
    return <MethodStep login={login} />;
  }

  return (
    <WizardFrame step1Done={false} step2Active={false}>
      <EmailStep login={login} />
    </WizardFrame>
  );
};

// Shared two-panel wizard chrome (branding + step indicator on the left,
// the active step's content on the right). Both onboarding steps render
// inside it so the frame stays identical between steps.
const WizardFrame: React.FC<{ step1Done: boolean; step2Active: boolean; children: React.ReactNode }> = ({ step1Done, step2Active, children }) => (
  <div className="fixed inset-0 bg-[--Light-Grey] overflow-y-auto py-[24px] flex flex-col">
    <div className="w-full max-w-[760px] mx-auto my-auto loginBanner rounded-[4px] overflow-hidden flex flex-row items-stretch bg-white">
      {/* Left panel — branding + step indicator */}
      <div className="bg-[--Primetime-main] text-white p-[28px] flex flex-col gap-[14px] w-[300px] flex-shrink-0">
        <img src="/assets/img/logo_lg.png" className="w-[140px]" />
        <h2 className="text-[20px] font-bold m-0 mt-[24px]">Welcome</h2>
        <p className="text-[13px] opacity-90 m-0 leading-[18px]">
          Let's finish setting up your account. Two quick steps.
        </p>
        <div className="flex flex-col gap-[10px] mt-[16px]">
          <StepDot active={!step1Done} done={step1Done} label="1. Confirm your email" />
          <StepDot active={step2Active} done={false} label="2. Set up two-factor auth" />
        </div>
      </div>
      {/* Right panel — current step content */}
      <div className="bg-white p-[28px] flex flex-col gap-[14px] flex-1 min-w-0">
        {/* Escape hatch. Onboarding is otherwise a dead end: a first-time user
            who signed in with the wrong account, or who can't reach the email
            on file, has no way out and can't even get back to the login form.
            Logging out drops status to 0, so App.tsx re-renders <Login/>. */}
        <span onClick={() => store.performLogout()}
              className="flex items-center gap-[4px] text-[--Dark-Grey] text-[13px] cursor-pointer hover:underline w-fit">
          <span className="material-icons text-[16px]">arrow_back</span>
          Back to login
        </span>
        {children}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Step 2 — 2FA method choice. The user picks either the authenticator app
// (drops into EnrollTwoFA: QR → 6-digit code → confirm) or email login
// codes (records preferred_2fa_method='email' server-side). Either path
// flips the App.tsx gate on the descriptor refresh, routing to Home.
// ---------------------------------------------------------------------------

// A mid-enrollment refresh leaves a live enrollTwoFA_session behind.
// Re-enter the authenticator path directly so EnrollTwoFA can resume it,
// rather than bouncing the user back to the choice cards.
const hasActiveEnrollment = (): boolean => {
  try {
    const raw = sessionStorage.getItem('enrollTwoFA_session');
    if (!raw) return false;
    const s = JSON.parse(raw);
    return !!(s.expires_at && new Date(s.expires_at).getTime() > Date.now());
  } catch (_e) {
    return false;
  }
};

const MethodStep: React.FC<{ login: any }> = ({ login }) => {
  const [choice, setChoice] = React.useState<'totp' | null>(hasActiveEnrollment() ? 'totp' : null);
  const [busy, setBusy] = React.useState(false);

  // Authenticator path — EnrollTwoFA owns its own QR/code/confirm flow and
  // session-resume. onCancel returns to the choice cards so the user can
  // switch to email codes instead.
  if (choice === 'totp') {
    return (
      <div className="fixed inset-0 bg-[--Light-Grey] overflow-y-auto">
        <EnrollTwoFA onComplete={() => { /* gate advances on refresh */ }}
                     completeLabel="Finish"
                     onCancel={() => setChoice(null)} />
      </div>
    );
  }

  // Email path — record the choice; setPreferred2FAMethod refreshes the
  // descriptor (preferred_2fa_method='email') so the gate routes to Home.
  // On failure we re-enable the cards rather than leaving them stuck.
  const chooseEmail = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await store.setPreferred2FAMethod('email');
    if (!ok) {
      store.dispatchError("Couldn't save your choice. Please try again.");
      setBusy(false);
    }
    // On success this component unmounts as the gate advances — leave busy set.
  };

  const verifiedEmail: string = login?.email || "your verified address";

  return (
    <WizardFrame step1Done={true} step2Active={true}>
      <h1 className="text-[20px] font-bold m-0">Set up two-factor authentication</h1>
      <p className="text-[13px] text-[--Dark-Grey] m-0 leading-[18px]">
        Choose how you'd like to receive your login code each time you sign in.
      </p>

      <MethodCard icon="smartphone"
                  title="Authenticator app"
                  description="Use Google Authenticator, 1Password, etc. Most secure."
                  disabled={busy}
                  onClick={() => setChoice('totp')} />

      <MethodCard icon="mail"
                  title="Email codes"
                  description={`We'll email a 6-digit code to ${verifiedEmail} at each login.`}
                  disabled={busy}
                  onClick={chooseEmail} />
    </WizardFrame>
  );
};

const MethodCard: React.FC<{ icon: string; title: string; description: string; disabled: boolean; onClick: () => void }> = ({ icon, title, description, disabled, onClick }) => (
  <button type="button"
          disabled={disabled}
          onClick={onClick}
          className="flex flex-row items-center gap-[16px] w-full text-left p-[16px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] bg-white hover:border-[--Primetime-main] hover:bg-[--Light-Grey] disabled:opacity-60 disabled:cursor-default cursor-pointer">
    <span className="material-icons !text-[--Primetime-main] text-[24px]">{icon}</span>
    <span className="flex flex-col gap-[2px]">
      <span className="text-[14px] font-bold text-black">{title}</span>
      <span className="text-[12px] text-[--Dark-Grey] leading-[16px]">{description}</span>
    </span>
  </button>
);

const StepDot: React.FC<{ active: boolean; done: boolean; label: string }> = ({ active, done, label }) => (
  <div className="flex flex-row items-center gap-[8px]">
    <span className={`material-icons text-[18px] ${done ? '!text-white' : active ? '!text-white' : '!text-white/40'}`}>
      { done ? 'check_circle' : active ? 'radio_button_checked' : 'radio_button_unchecked' }
    </span>
    <span className={`text-[13px] ${active || done ? 'text-white' : 'text-white/60'}`}>{label}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Step 1 — email confirm (editable). Reuses emailApi: start_verify/verify
// for the unchanged address, request_change/confirm_change (password) for
// an edited address.
// ---------------------------------------------------------------------------
const EmailStep: React.FC<{ login: ReturnType<typeof useAppSelector> extends never ? never : any }> = ({ login }) => {
  const currentEmail: string = login?.email || "";
  const pendingEmail: string = login?.pending_email || "";
  const pendingExpiresAt: number = login?.pending_email_expires_at || 0;
  const pendingAlive = !!pendingEmail && (pendingExpiresAt === 0 || pendingExpiresAt * 1000 > Date.now());

  const [email, setEmail] = React.useState<string>(pendingAlive ? pendingEmail : currentEmail);
  const [password, setPassword] = React.useState<string>("");
  const [code, setCode] = React.useState<string>("");
  // 'enter' = editing the address; 'code' = a code has been sent.
  const [phase, setPhase] = React.useState<'enter' | 'code'>(pendingAlive ? 'code' : 'enter');
  const [busy, setBusy] = React.useState(false);
  const cooldown = useResendCooldown(RESEND_COOLDOWN_SECONDS);

  const dirty = email.trim().toLowerCase() !== currentEmail.trim().toLowerCase();
  const emailInvalid = !emailRegex.test(email.trim());
  const codeInvalid = !sixDigitRegex.test(code.trim());

  const sendCode = React.useCallback(async () => {
    if (emailInvalid || busy) return;
    setBusy(true);
    try {
      const res = dirty
        ? await requestEmailChange(email.trim(), password)
        : await startVerifyEmail();
      if (res.code === 0) {
        setPhase('code');
        setCode("");
        cooldown.arm();
      } else {
        store.dispatchError(res.error || "Could not send the code.");
      }
    } finally {
      setBusy(false);
    }
  }, [dirty, email, password, emailInvalid, busy, cooldown]);

  const submitCode = React.useCallback(async () => {
    if (codeInvalid || busy) return;
    setBusy(true);
    try {
      const res = dirty ? await confirmEmailChange(code.trim()) : await verifyEmail(code.trim());
      if (res.code === 0) {
        store.dispatchSuccess("Email verified.");
        // Refresh the descriptor: email_verified flips true, so the wizard
        // advances from this email step to the authenticator-setup step on
        // re-render.
        await store.getLogin();
      } else if (res.code === 32) {
        // No pending / expired code (codeEmailNoPending). Drop back to the
        // entry step so the user can request a fresh code rather than
        // being stuck typing into a dead code field.
        store.dispatchError("Your code expired. Please request a new one.");
        setCode("");
        setPhase('enter');
      } else {
        store.dispatchError(res.error || "Invalid or expired code.");
        setCode("");
      }
    } finally {
      setBusy(false);
    }
  }, [dirty, code, codeInvalid, busy]);

  return (
    <>
      <h1 className="text-[20px] font-bold m-0">Confirm your email</h1>
      <p className="text-[13px] text-[--Dark-Grey] m-0 leading-[18px]">
        We'll use this address for security codes and account recovery. You can edit it before confirming.
      </p>

      <Input value={email}
             label="Email address"
             onValueChange={(v: string) => { setEmail(v); if (phase === 'code') setPhase('enter'); }}
             type="email"
             isDisabled={busy}
             placeholder="you@example.com"
             classNames={{
               inputWrapper: "px-[12px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey]",
             }} />

      { dirty && phase === 'enter' &&
        <Input value={password}
               label="Current password"
               onValueChange={setPassword}
               type="password"
               isDisabled={busy}
               placeholder="Required to change your email"
               classNames={{
                 inputWrapper: "px-[12px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey]",
               }} />
      }

      { phase === 'code' &&
        <>
          <p className="text-[13px] text-[--Dark-Grey] m-0">
            Enter the 6-digit code we sent to <strong>{email.trim()}</strong>.
          </p>
          <DigitInput value={code} onChange={setCode} disabled={busy} autoFocus />
          <div className="flex flex-row justify-between items-center">
            <span onClick={() => { if (cooldown.ready && !busy) sendCode(); }}
                  className={`text-[13px] ${
                    !cooldown.ready
                      ? 'text-[--Error] cursor-default'
                      : busy
                        ? 'text-[--Mid-Grey] cursor-default'
                        : 'text-[--Dark-Grey] cursor-pointer hover:underline'
                  }`}>
              { cooldown.ready ? 'Resend code' : `Resend in ${cooldown.display}` }
            </span>
            <Button caption={busy ? "Verifying…" : "Verify email"}
                    layout={codeInvalid || busy ? 'warning_bg' : 'default'}
                    isDisabled={codeInvalid || busy}
                    onClick={submitCode} />
          </div>
        </>
      }

      { phase === 'enter' &&
        <div className="flex flex-row justify-end mt-[4px]">
          <Button caption={busy ? "Sending…" : "Send code"}
                  layout={emailInvalid || (dirty && password.length === 0) || busy ? 'warning_bg' : 'default'}
                  isDisabled={emailInvalid || (dirty && password.length === 0) || busy}
                  onClick={sendCode} />
        </div>
      }
    </>
  );
};

export default OnboardingWizard;
